import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { organizationsApi, type Organization } from 'lib/api'
import { formatDate } from 'lib/utils'
import {
  Plus,
  Search,
  Building2,
  Edit3,
  Trash2,
  Globe,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  XCircle,
  Users,
  Briefcase,
  UserCheck,
  BarChart3,
  Zap,
  DollarSign
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { CreateOrganizationModal } from '../components/CreateOrganizationModal'
import { EditOrganizationModal } from '../components/EditOrganizationModal'
import { OrganizationProfileModal } from '../components/OrganizationProfileModal'
import { PageHeader } from 'components/layout/PageHeader'

export function Organizations() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { hasPermission, user } = useAuth()

  // Check if user is super admin (can create organizations)
  const isSuperAdmin = user?.is_superuser || user?.email === 'admin@selektia.ma'
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingOrganization, setDeletingOrganization] = useState<Organization | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [viewingOrganization, setViewingOrganization] = useState<Organization | null>(null)
  const [updatingStatusOrgId, setUpdatingStatusOrgId] = useState<number | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(12)

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true)
      const response = await organizationsApi.getAll()
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data as any)?.results || []
      setOrganizations(data)
    } catch (error) {
      console.error('Error fetching organizations:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('organizations.messages.fetchFailed', 'Failed to load organizations'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }, [addToast, t])

  useEffect(() => {
    fetchOrganizations()
  }, [fetchOrganizations])

  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch =
      (org.name && org.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (org.domain && org.domain.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (org.email && org.email.toLowerCase().includes(searchTerm.toLowerCase()))

    if (!matchesSearch) return false

    // Status filter - check both is_active and status field
    if (statusFilter === 'active') {
      if (!org.is_active || org.status !== 'active') return false
    }
    if (statusFilter === 'inactive') {
      if (org.is_active && org.status === 'active') return false
    }

    return true
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentOrganizations = filteredOrganizations.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter])

  const handleDelete = async () => {
    if (!deletingOrganization) return

    try {
      setDeleting(true)
      await organizationsApi.delete(deletingOrganization.id)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('organizations.messages.deleted', 'Organization deleted successfully'),
        duration: 3000
      })
      setShowDeleteConfirm(false)
      setDeletingOrganization(null)
      fetchOrganizations()
    } catch (error: any) {
      console.error('Error deleting organization:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || t('organizations.messages.deleteFailed', 'Failed to delete organization'),
        duration: 5000
      })
    } finally {
      setDeleting(false)
    }
  }

  const openDeleteConfirm = (org: Organization) => {
    setDeletingOrganization(org)
    setShowDeleteConfirm(true)
  }

  const openEditModal = (org: Organization) => {
    setEditingOrganization(org)
    setShowEditModal(true)
  }

  const openProfileModal = (org: Organization) => {
    setViewingOrganization(org)
    setShowProfileModal(true)
  }

  const handleOrganizationUpdated = (updatedOrg: Organization) => {
    setOrganizations(prev => prev.map(org => org.id === updatedOrg.id ? updatedOrg : org))
    setShowEditModal(false)
    setEditingOrganization(null)
  }

  const handleToggleActive = async (org: Organization, newValue: boolean) => {
    const orgId = org.id
    const previousValue = org.is_active !== false
    const previousStatus = org.status

    // Determine new status based on activation
    const newStatus: Organization['status'] = newValue ? 'active' : (previousStatus === 'active' ? 'suspended' : previousStatus || 'suspended')

    // Optimistic update in UI
    setOrganizations(prev => prev.map(o =>
      o.id === orgId ? ({ ...o, is_active: newValue, status: newStatus } as Organization) : o
    ))

    // Also update viewing organization if modal is open
    if (viewingOrganization && viewingOrganization.id === orgId) {
      setViewingOrganization(prev => prev ? ({ ...prev, is_active: newValue, status: newStatus } as Organization) : prev)
    }

    setUpdatingStatusOrgId(orgId)

    try {
      const response = await organizationsApi.update(orgId, { is_active: newValue, status: newStatus })
      // Update with server response
      setOrganizations(prev => prev.map(o => o.id === orgId ? response.data : o))

      // Update viewing organization if modal is open
      if (viewingOrganization && viewingOrganization.id === orgId) {
        setViewingOrganization(response.data)
      }

      addToast({
        type: 'success',
        title: t('common.success'),
        message: newValue
          ? t('organizations.messages.activated', 'Organization activated successfully')
          : t('organizations.messages.deactivated', 'Organization deactivated successfully'),
        duration: 3000
      })
    } catch (error: any) {
      console.error('Error updating organization status:', error)
      // Revert on error
      setOrganizations(prev => prev.map(o =>
        o.id === orgId ? ({ ...o, is_active: previousValue, status: previousStatus } as Organization) : o
      ))

      // Also revert viewing organization if modal is open
      if (viewingOrganization && viewingOrganization.id === orgId) {
        setViewingOrganization(prev => prev ? ({ ...prev, is_active: previousValue, status: previousStatus } as Organization) : prev)
      }

      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || error.response?.data?.detail || t('organizations.messages.toggleFailed', 'Failed to update organization status'),
        duration: 5000
      })
    } finally {
      setUpdatingStatusOrgId(null)
    }
  }

  const handleStatusChange = async (org: Organization, newStatus: Organization['status']) => {
    const orgId = org.id
    const previousStatus = org.status
    const newIsActive = newStatus === 'active'

    // Optimistic update in UI
    setOrganizations(prev => prev.map(o =>
      o.id === orgId ? ({ ...o, status: newStatus, is_active: newIsActive } as Organization) : o
    ))

    // Also update viewing organization if modal is open
    if (viewingOrganization && viewingOrganization.id === orgId) {
      setViewingOrganization(prev => prev ? ({ ...prev, status: newStatus, is_active: newIsActive } as Organization) : prev)
    }

    setUpdatingStatusOrgId(orgId)

    try {
      const response = await organizationsApi.update(orgId, { status: newStatus, is_active: newIsActive })
      // Update with server response
      setOrganizations(prev => prev.map(o => o.id === orgId ? response.data : o))

      // Update viewing organization if modal is open
      if (viewingOrganization && viewingOrganization.id === orgId) {
        setViewingOrganization(response.data)
      }

      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('organizations.messages.statusUpdated', 'Organization status updated successfully'),
        duration: 3000
      })
    } catch (error: any) {
      console.error('Error updating organization status:', error)
      // Revert on error
      setOrganizations(prev => prev.map(o =>
        o.id === orgId ? ({ ...o, status: previousStatus, is_active: org.is_active } as Organization) : o
      ))

      // Also revert viewing organization if modal is open
      if (viewingOrganization && viewingOrganization.id === orgId) {
        setViewingOrganization(prev => prev ? ({ ...prev, status: previousStatus, is_active: org.is_active } as Organization) : prev)
      }

      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || error.response?.data?.detail || t('organizations.messages.statusUpdateFailed', 'Failed to update organization status'),
        duration: 5000
      })
    } finally {
      setUpdatingStatusOrgId(null)
    }
  }

  const getStatusCount = (status: 'all' | 'active' | 'inactive') => {
    if (status === 'all') return organizations.length
    if (status === 'active') return organizations.filter(o => o.is_active).length
    return organizations.filter(o => !o.is_active).length
  }

  const getLogoUrl = (logo?: string): string | null => {
    if (!logo) return null
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo
    // Media files are served from /media/, not /api/media/
    const envUrl = process.env.REACT_APP_API_URL
    const baseUrl = envUrl && envUrl.trim().length > 0
      ? envUrl.trim().replace(/\/+$/, '')
      : `http://${window.location.hostname}:8000`
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`
    return `${baseUrl}/media${logoPath}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-[1149px]:space-y-4 max-[1149px]:px-4 max-[1149px]:py-4">
      <PageHeader
        title={t('organizations.title', 'Organizations')}
        actions={
          isSuperAdmin ? (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 max-[1149px]:w-full max-[1149px]:justify-center"
            >
              <Plus className="h-4 w-4" />
              <span>{t('organizations.create', 'Create Organization')}</span>
            </Button>
          ) : undefined
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">{t('organizations.stats.total', 'Total Organizations')}</p>
                <p className="text-xl font-bold">{getStatusCount('all')}</p>
              </div>
              <Building2 className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">{t('organizations.stats.active', 'Active')}</p>
                <p className="text-xl font-bold">{getStatusCount('active')}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">{t('organizations.stats.inactive', 'Inactive')}</p>
                <p className="text-xl font-bold">{getStatusCount('inactive')}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('organizations.searchPlaceholder', 'Search organizations...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                className="px-3 py-2 border rounded-md bg-background text-foreground"
              >
                <option value="all">{t('organizations.filters.all', 'All')}</option>
                <option value="active">{t('organizations.filters.active', 'Active')}</option>
                <option value="inactive">{t('organizations.filters.inactive', 'Inactive')}</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Organizations List */}
      {currentOrganizations.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {t('organizations.noOrganizations', 'No organizations found')}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t('organizations.noOrganizationsDescription', 'Get started by creating your first organization')}
            </p>
            {isSuperAdmin && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('organizations.create', 'Create Organization')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentOrganizations.map((org) => (
            <Card
              key={org.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => openProfileModal(org)}
            >
              <CardHeader className="border-b">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getLogoUrl(org.logo) ? (
                        <img
                          src={getLogoUrl(org.logo)!}
                          alt={org.name}
                          className="h-8 w-8 object-contain rounded"
                          onError={(e) => {
                            // Fallback to icon if image fails to load
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      ) : (
                        <Building2 className="h-5 w-5 text-primary" />
                      )}
                      <CardTitle className="text-lg">{org.name}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={org.is_active !== false ? 'default' : 'secondary'}
                        className={
                          org.status === 'suspended' 
                            ? 'bg-yellow-500 text-white hover:bg-yellow-600 border-transparent'
                            : org.status === 'cancelled'
                            ? 'bg-orange-500 text-white hover:bg-orange-600 border-transparent'
                            : org.status === 'payment_failed'
                            ? 'bg-red-500 text-white hover:bg-red-600 border-transparent'
                            : org.status === 'terminated'
                            ? 'bg-gray-500 text-white hover:bg-gray-600 border-transparent'
                            : org.is_active !== false
                            ? 'bg-green-500 text-white hover:bg-green-600 border-transparent'
                            : ''
                        }
                      >
                        {org.status === 'suspended'
                          ? t('organizations.status.suspended', 'Suspended')
                          : org.status === 'cancelled'
                          ? t('organizations.status.cancelled', 'Cancelled')
                          : org.status === 'payment_failed'
                          ? t('organizations.status.paymentFailed', 'Payment Failed')
                          : org.status === 'terminated'
                          ? t('organizations.status.terminated', 'Terminated')
                          : org.is_active !== false
                          ? t('organizations.status.active', 'Active')
                          : t('organizations.status.inactive', 'Inactive')
                        }
                      </Badge>
                    </div>
                  </div>
                  {org.subscription_plan && (
                    <Badge
                      variant="default"
                      className={
                        org.subscription_plan.toLowerCase() === 'starter'
                          ? 'bg-blue-500 text-white hover:bg-blue-600 border-transparent'
                          : org.subscription_plan.toLowerCase() === 'premium'
                            ? 'bg-purple-500 text-white hover:bg-purple-600 border-transparent'
                            : org.subscription_plan.toLowerCase() === 'advanced'
                              ? 'bg-orange-500 text-white hover:bg-orange-600 border-transparent'
                              : ''
                      }
                    >
                      {org.subscription_plan.charAt(0).toUpperCase() + org.subscription_plan.slice(1)}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {org.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{org.description}</p>
                )}
                <div className="space-y-2 text-sm">
                  {org.domain && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-4 w-4" />
                      <span>{org.domain}</span>
                    </div>
                  )}
                  {org.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span>{org.email}</span>
                    </div>
                  )}
                  {org.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{org.phone}</span>
                    </div>
                  )}
                  {org.daily_cv_processing_limit !== undefined && org.daily_cv_processing_limit > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <span>{t('organizations.dailyCvLimit', 'Daily CV Limit')}: {org.daily_cv_processing_limit}</span>
                    </div>
                  )}
                  {org.daily_job_offer_limit !== undefined && org.daily_job_offer_limit > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <span>{t('organizations.dailyOfferLimit', 'Daily Offer Limit')}: {org.daily_job_offer_limit}</span>
                    </div>
                  )}
                  {org.subscription_deadline && (
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <span>{t('organizations.deadline', 'Deadline')}: {formatDate(org.subscription_deadline)}</span>
                    </div>
                  )}
                  {org.subscription_duration_days && org.subscription_duration_days > 0 && org.subscription_duration_type === 'days' && (
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <span>{t('organizations.duration', 'Duration')}: {org.subscription_duration_days} {t('organizations.days', 'days')}</span>
                    </div>
                  )}
                  {org.number_of_users !== undefined && org.number_of_users > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" />
                      <span>{t('organizations.users', 'Users')}: {org.number_of_users}</span>
                    </div>
                  )}
                  {org.consumption && (
                    <div className="pt-2 mt-2 border-t space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 text-amber-600 font-medium">
                          <Zap className="h-3 w-3" />
                          <span>AI Tokens</span>
                        </div>
                        <div className="flex gap-2 text-muted-foreground">
                          <span>In: {((org.consumption.total_candidate_input_tokens || 0) + (org.consumption.total_offer_input_tokens || 0)).toLocaleString()}</span>
                          <span>Out: {((org.consumption.total_candidate_output_tokens || 0) + (org.consumption.total_offer_output_tokens || 0)).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 text-primary font-bold">
                          <DollarSign className="h-3 w-3" />
                          <span>{t('organizations.cost', 'Total cost')}</span>
                        </div>
                        <span className="font-bold text-primary">${org.consumption.total_cost?.toFixed(8) || '0.000000'}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="pt-2 border-t flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {t('organizations.created', 'Created')}: {formatDate(org.created_at)}
                  </span>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/organizations/${org.id}/stats`)}
                      title={t('organizations.viewStats', 'View Statistics')}
                    >
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(org)}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDeleteConfirm(org)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setDeletingOrganization(null)
        }}
        title={t('organizations.deleteOrganizationTitle', 'Delete Organization')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('organizations.deleteOrganizationConfirm', { name: deletingOrganization?.name })}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false)
                setDeletingOrganization(null)
              }}
              disabled={deleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {t('organizations.deleteOrganizationCta', 'Delete Organization')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Organization Modal */}
      <CreateOrganizationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onOrganizationCreated={() => {
          fetchOrganizations()
          setShowCreateModal(false)
        }}
      />

      {/* Edit Organization Modal */}
      <EditOrganizationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setEditingOrganization(null)
        }}
        organization={editingOrganization}
        onOrganizationUpdated={handleOrganizationUpdated}
      />

      {/* Organization Profile Modal */}
      <OrganizationProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false)
          setViewingOrganization(null)
        }}
        organization={viewingOrganization}
        onToggleActive={handleToggleActive}
        onStatusChange={handleStatusChange}
        updatingStatusOrgId={updatingStatusOrgId}
      />
    </div>
  )
}

